import "./voucherdialogs.scss";
import * as React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type ThunkDispatch } from "redux-thunk";
import { finishSale } from "../../../../Actions/SellScreen/SellScreenActions";
import { addPaymentToSale } from "../../../../Actions/SellScreen/SellScreenPaymentActions";
import Button from "../../../../Common/Buttons/Button";
import UnescapedContent from "../../../../Common/Misc/UnescapedContent";
import { type LocalDatabasePaymentMethod } from "../../../../Data/_Bases/BasePaymentMethodRepository";
import { type InjectedDataSourceProps, withDataSource } from "../../../../Data/DataStores/WithDataSource";
import { type SaleVoucherProduct } from "../../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { type RootState } from "../../../../Reducers/ShopfrontReducers";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";

function mapStateToProps(state: RootState): {
    register: null | string;
} {
    return {
        register: state.location.register ? state.location.register.id : null,
    };
}

const connector = connect(mapStateToProps);

interface Props extends ConnectedProps<typeof connector>, InjectedDataSourceProps {
    dispatch: ThunkDispatch<any, any, any>;
    vouchers: Array<SaleVoucherProduct>;
    register: string | null;
}

interface State {
    loading: boolean;
    description: string;
    methods: Array<LocalDatabasePaymentMethod>;
}

export class VoucherActivateMethodDialog extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            loading: true,
            description:
                "Select Payment Method and then swipe the Gift Card." +
                "\nSelect \"Savings\" and use \"0000\" for the pin.\nNote, manager's password may be required",
            methods: [],
        };

        this.handleMethodClick = this.handleMethodClick.bind(this);
        this.loadMethods = this.loadMethods.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
    }

    public componentDidMount(): void {
        this.loadMethods();
    }

    protected loadMethods(): Promise<void> {
        this.setState({
            loading: true,
        });

        return this.props.data
            .then(async source => {
                if(!this.props.register) {
                    throw new Error("No register is currently active");
                }

                const register = await source.registers.get(this.props.register);

                const promises = [];

                promises.push(
                    source.paymentMethods.getByType([ "eftpos", "pc-eftpos", "tyro" ])
                        .then(methods => methods.filter(method => register.payment_methods.includes(method.uuid)))
                        .then(methods => {
                            methods.sort((a, b) => a.name.localeCompare(b.name));

                            this.setState({
                                methods: methods,
                            });
                        })
                );

                promises.push(
                    source.products.get(this.props.vouchers[0].uuid)
                        .then(product => {
                            if(product.description) {
                                this.setState({
                                    description: product.description,
                                });
                            }
                        })
                );

                return Promise.all(promises);
            })
            .then(() => {
                this.setState({
                    loading: false,
                });
            });
    }

    protected handleMethodClick(method: LocalDatabasePaymentMethod): void {
        this.props.dispatch(addPaymentToSale(method.uuid, -this.props.vouchers[0].prices.price, {
            cashout      : false,
            exact        : false,
            rounding     : 0,
            refund       : true,
            vouchers     : this.props.vouchers,
            approve      : true,
            voucherRefund: true,
        }));
    }

    protected handleCancelClick(e: React.MouseEvent<HTMLButtonElement>): void {
        e.stopPropagation();

        this.props.dispatch(finishSale("COMPLETED", {
            vouchers     : this.props.vouchers,
            voucherFailed: true,
            voucherCancel: true,
        }));
    }

    public render(): React.ReactNode {
        if(this.state.loading) {
            return (
                <FontAwesomeLight icon="spinner-third" spin />
            );
        }

        return (
            <div className="voucher-activate-method">
                <div className="voucher-activate-heading">
                    Activate Voucher
                </div>
                <div className="voucher-instructions">
                    Please select the payment method to activate the <strong>{this.props.vouchers[0].name}</strong>?
                </div>
                <div className="voucher-description">
                    <UnescapedContent>
                        {this.state.description}
                    </UnescapedContent>
                </div>
                <div className="voucher-methods">
                    {this.state.methods.map(method => (
                        <Button
                            key={method.uuid}
                            onClick={(): void => {
                                this.handleMethodClick(method);
                            }}
                            style={{
                                background : method.background_colour,
                                color      : method.text_colour,
                                borderColor: "#000000",
                            }}
                        >
                            {method.name}
                        </Button>
                    ))}
                    <Button
                        type="button"
                        classes={[ "voucher-button-cancel" ]}
                        onClick={this.handleCancelClick}
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        );
    }
}

export default connector(withDataSource(VoucherActivateMethodDialog));
